import Clarity from "@microsoft/clarity";

// Microsoft Clarity Project ID
const CLARITY_PROJECT_ID = "qc0yjtnd9w";

// Track if Clarity has been initialized
let isInitialized = false;

/**
 * Initialize Microsoft Clarity with the project ID
 */
export const initializeClarity = (): void => {
  if (isInitialized) {
    console.warn("Clarity is already initialized");
    return;
  }

  try {
    Clarity.init(CLARITY_PROJECT_ID);
    isInitialized = true;
    console.log("Microsoft Clarity initialized successfully");
  } catch (error) {
    console.error("Failed to initialize Microsoft Clarity:", error);
  }
};

/**
 * Identify a user in Clarity
 * @param userId - The unique identifier for the user
 * @param sessionId - Optional custom session identifier
 * @param pageId - Optional custom page identifier
 * @param friendlyName - Optional friendly name for the user
 */
export const identifyUser = (
  userId: string,
  sessionId?: string,
  pageId?: string,
  friendlyName?: string,
): void => {
  if (!isInitialized) {
    console.warn("Clarity not initialized. Call initializeClarity() first.");
    return;
  }

  try {
    Clarity.identify(userId, sessionId, pageId, friendlyName);
  } catch (error) {
    console.error("Failed to identify user in Clarity:", error);
  }
};

/**
 * Set a custom tag in Clarity
 * @param key - The key for the tag
 * @param value - The value(s) for the tag
 */
export const setTag = (key: string, value: string | string[]): void => {
  if (!isInitialized) {
    console.warn("Clarity not initialized. Call initializeClarity() first.");
    return;
  }

  try {
    Clarity.setTag(key, value);
  } catch (error) {
    console.error("Failed to set tag in Clarity:", error);
  }
};

/**
 * Track a custom event in Clarity
 * @param eventName - The name of the event to track
 */
export const trackEvent = (eventName: string): void => {
  if (!isInitialized) {
    console.warn("Clarity not initialized. Call initializeClarity() first.");
    return;
  }

  try {
    Clarity.event(eventName);
  } catch (error) {
    console.error("Failed to track event in Clarity:", error);
  }
};

/**
 * Set cookie consent for Clarity
 * @param hasConsent - Whether the user has given consent
 */
export const setConsent = (hasConsent: boolean = true): void => {
  if (!isInitialized) {
    console.warn("Clarity not initialized. Call initializeClarity() first.");
    return;
  }

  try {
    Clarity.consent(hasConsent);
  } catch (error) {
    console.error("Failed to set consent in Clarity:", error);
  }
};

/**
 * Upgrade a session for priority recording
 * @param reason - The reason for upgrading the session
 */
export const upgradeSession = (reason: string): void => {
  if (!isInitialized) {
    console.warn("Clarity not initialized. Call initializeClarity() first.");
    return;
  }

  try {
    Clarity.upgrade(reason);
  } catch (error) {
    console.error("Failed to upgrade session in Clarity:", error);
  }
};

// Predefined event names for consistency
export const ClarityEvents = {
  // Authentication events
  USER_SIGNED_IN: "user_signed_in",
  USER_SIGNED_OUT: "user_signed_out",

  // Navigation events
  VIEW_CHANGED: "view_changed",
  SEARCH_PERFORMED: "search_performed",
  SEARCH_CLEARED: "search_cleared",

  // Document events
  DOCUMENT_OPENED: "document_opened",
  DOCUMENT_DOWNLOADED: "document_downloaded",
  DOCUMENT_SYNC_REQUESTED: "document_sync_requested",

  // App events
  APP_UPDATED: "app_updated",
  OFFLINE_MODE: "offline_mode",
  SYNC_COMPLETED: "sync_completed",
  HELP_ACCESSED: "help_accessed",

  // Error events
  SYNC_ERROR: "sync_error",
  DOWNLOAD_ERROR: "download_error",
} as const;

// Predefined tag keys for consistency
export const ClarityTags = {
  CURRENT_VIEW: "current_view",
  USER_TYPE: "user_type",
  ONLINE_STATUS: "online_status",
  DOCUMENT_COUNT: "document_count",
  SEARCH_QUERY: "search_query",
} as const;
